import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface StatusFormData {
  name: string;
  description: string;
}

export const useStatusOperations = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const createStatus = async (formData: StatusFormData) => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from("policy_statuses")
        .insert([formData]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Status created successfully",
      });
    } catch (error) {
      console.error("Error creating status:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create status",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (id: string, formData: StatusFormData) => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from("policy_statuses")
        .update(formData)
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Status updated successfully",
      });
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update status",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteStatus = async (id: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from("policy_statuses")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Status deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting status:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete status",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createStatus,
    updateStatus,
    deleteStatus,
    isLoading,
  };
};